import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/foam-roof-replacement-cost-phoenix";
const TITLE = "Foam Roof Replacement Cost in Phoenix (2026) | Durafoam Roofing";
const DESCRIPTION =
  "What a new SPF foam roof actually costs in Phoenix — per-square-foot pricing, what drives the number, and how to know if you really need a replacement.";

const faqs = [
  { q: "How much does a new foam roof cost in Phoenix?", a: "Most full SPF foam roof installations run $5.00–$9.00 per square foot installed, based on 1\" of foam — our standard thickness. That price includes the foam, two coats of elastomeric, and standard penetrations. Your total depends on your actual roof area, so we measure on site rather than quote from a satellite image." },
  { q: "How long does a foam roof last?", a: "30+ years for the foam itself in Phoenix. New installations come with a 5-year or 10-year warranty, and after that we recommend recoating every 5 years to maintain the warranty and protect the foam. Many Phoenix foam roofs from the 1990s are still going strong on that schedule." },
  { q: "Do I really need a replacement, or just a recoat?", a: "If your foam is intact, almost always just a recoat. We only recommend full replacement when foam is water-damaged across large areas or has lost structural integrity. We'll show you photos." },
  { q: "Can foam go over my existing roof?", a: "Often yes — over old built-up, mineral cap, and some single-ply systems. Skipping tear-off saves real money. We assess the substrate first." },
];

export const Route = createFileRoute("/foam-roof-replacement-cost-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Foam Roof Replacement",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="foam-roof-replacement-cost-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Foam Roof Replacement Cost in Phoenix"
      subhead="Real 2026 price ranges for new SPF foam roof installations across the Phoenix Valley — with what actually drives the number."
      heroBullets={[
        "Typical range: $5.00–$9.00 per sq ft (1\" foam)",
        "Priced off your measured roof area",
        "30+ year service life with recoats",
        "Includes foam + two-coat elastomeric",
      ]}
      intro={
        <>
          <p>
            A new spray polyurethane foam (SPF) roof in Phoenix typically costs{" "}
            <strong>$5.00–$9.00 per square foot installed</strong>, based on <strong>1" of
            foam</strong> — our standard thickness. That covers the foam, two coats of elastomeric,
            and standard penetrations.
          </p>
          <p>
            We price off measured roof area rather than the home's living area, which is why we
            don't publish a flat price for a "2,000 sq ft home" — the roof surface, slope, and
            detail work vary too much between Phoenix houses of the same size.
          </p>
          <p>
            1" is what we install on the majority of Phoenix homes. We'll spec <strong>1.5"</strong>{" "}
            in specific cases — like a plank wood deck — and <strong>2"+</strong> when you want
            extra R-value. Tear-off, if required, is additional.
          </p>
          <p>
            Before you replace, get a real second opinion. We honestly turn down more
            replacement jobs than we sell — most "needs replacement" roofs are still solid foam
            that just need a recoat for a fraction of the price.
          </p>
        </>
      }
      pricingRows={[
        { label: "Foam roof install (per sq ft)", range: "$5.00–$9.00", notes: "1\" foam + 2-coat elastomeric system" },
        { label: "Over an existing substrate", range: "Low end of the range", notes: "No tear-off when the roof is a candidate" },
        { label: "Heavy detail work", range: "High end of the range", notes: "Many AC stands, skylights, parapets" },
        { label: "Commercial (per sq ft)", range: "$5.00–$9.00", notes: "Larger jobs, volume pricing" },
        { label: "Tear-off (if required)", range: "+$1.00–$2.50 / sq ft", notes: "Disposal & substrate prep" },
      ]}
      factors={[
        { title: "Foam thickness", body: "1\" is our standard residential spec. 1.5\" goes on specific substrates like plank wood decks; 2\"+ when you want added R-value. We size to your home's needs, not a default." },
        { title: "Tear-off vs. recover", body: "Going over an existing serviceable substrate saves $1–$2.50 per sq ft. We test for moisture and adhesion before recommending recover." },
        { title: "Coating system", body: "Acrylic is base. Silicone or urethane add cost but extend the recoat cycle and improve ponding tolerance." },
        { title: "Penetrations & details", body: "AC stands, vents, skylights, and parapet walls add hand-detail labor. More obstacles, higher per-foot cost." },
      ]}
      saveMoney={{
        heading: "How to keep replacement affordable",
        body: (
          <>
            <ul>
              <li><strong>Get a real second opinion.</strong> Many roofs sold as "needing replacement" are recoat candidates at 1/3 the cost.</li>
              <li><strong>Recover when possible.</strong> Skipping tear-off can save $2,000–$5,000 on a typical home.</li>
              <li><strong>Schedule October–May.</strong> Summer monsoons delay foam install; off-season pricing is more flexible.</li>
              <li><strong>Then protect it.</strong> New installs come with a 5- or 10-year warranty. After that, recoat every 5 years to keep the warranty in force — a $3,000 recoat beats a $10,000 replacement.</li>
            </ul>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Get a real number for your roof"
      ctaBody="Use the instant calculator, or schedule a free inspection and we'll tell you honestly whether you need replacement or a recoat will do it."
    />
  );
}
